import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  FormField,
  IconFormField,
  PageHeading,
  CardFooterPrompt,
} from '@ai-tutor/ui';
import { Lock } from 'lucide-react';

import { XoneraSpinner } from '@/components/core/common/feedback/XoneraSpinner';
import {
  newPasswordSchema,
  PASSWORD_HINT,
  type NewPasswordForm,
} from '@/lib/schemas/auth';

/**
 * The "set a new password" card.
 *
 * One field, not two. It used to ask for the password and a confirmation of it;
 * with a reveal toggle on the field the confirmation is redundant, and on this
 * screen especially - the user arrived here precisely because they could not
 * remember a password.
 *
 * Lives here rather than inside `pages/auth/ResetPasswordPage` because the page
 * only reaches its `ready` state after Supabase resolves a recovery session from
 * the URL, which the catalog cannot produce deterministically. Keeping the card
 * in its own module lets `Components/Auth/Desktop/Layout/SetNewPasswordCard` story it directly
 * while `App/Auth/Desktop/Layout/ResetPassword` stories the page's other two states.
 */
export function SetNewPasswordCard({
  onSubmit,
  onBackToSignIn,
}: {
  onSubmit: (values: NewPasswordForm) => Promise<void>;
  onBackToSignIn: () => void;
}) {
  const form = useForm<NewPasswordForm>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { password: '' },
  });

  return (
    <div className='space-y-6'>
      <PageHeading
        title='Set a new password'
        subtitle='Choose a password for your Xonera account.'
      />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <IconFormField
                icon={Lock}
                label='New password'
                type='password'
                autoComplete='new-password'
                field={field}
              />
            )}
          />
          <p className='text-label text-muted-foreground'>{PASSWORD_HINT}</p>
          <Button
            type='submit'
            size='touch'
            fullWidth
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting && (
              <XoneraSpinner className='mr-2 h-4 w-4' label='' />
            )}
            Update password
          </Button>
        </form>
      </Form>
      <CardFooterPrompt
        prompt='Changed your mind?'
        actionLabel='Back to sign in'
        onAction={onBackToSignIn}
      />
    </div>
  );
}
